/**
 * Service de Histórico do Cronograma - CIPA Fácil
 *
 * Gerencia o histórico de alterações no cronograma para auditoria NR-05.
 * Nenhuma ação concluída pode ser deletada - apenas arquivada.
 *
 * @author CIPA Fácil Team
 * @date 2026-02-02
 */

import { supabase } from "../config/supabase";

/**
 * Tipos de ação no histórico
 */
export type AcaoHistorico =
  | "created"
  | "confirmed"
  | "updated"
  | "canceled"
  | "rescheduled"
  | "archived_before_recreate";

/**
 * Registro de histórico do cronograma
 */
export interface CronogramaHistorico {
  id?: string;
  empresa_id: string;
  mandato_id: string;
  reuniao_id?: string | null;
  fase_id?: string | null;
  acao_id?: string | null;
  acao: AcaoHistorico;
  dados_anteriores?: Record<string, unknown>;
  dados_novos?: Record<string, unknown>;
  motivo?: string;
  criado_por: string;
  created_at?: string;
}

/**
 * Registra uma ação no histórico do cronograma
 */
export async function registrarHistorico(
  historico: Omit<CronogramaHistorico, "id" | "created_at">,
): Promise<void> {
  const { error } = await supabase
    .from("cp_cronograma_historico")
    .insert([historico]);

  if (error) {
    console.error("[CronogramaHistorico] Erro ao registrar histórico:", error);
    throw new Error("Erro ao registrar histórico do cronograma");
  }
}

/**
 * Arquiva ações do cronograma antes de recriar (preserva evidência)
 */
export async function arquivarCronogramaAnterior(
  empresaId: string,
  mandatoId: string,
  userId: string,
  motivo: string = "Recriação do cronograma",
): Promise<void> {
  try {
    // 1. Buscar todas as fases da empresa/mandato
    const { data: fases, error: fasesError } = await supabase
      .from("cp_fases")
      .select("*")
      .eq("empresa_id", empresaId);

    if (fasesError) throw fasesError;

    // 2. Buscar todas as ações das fases
    const { data: acoes, error: acoesError } = await supabase
      .from("cp_acoes_cronograma")
      .select("*")
      .eq("empresa_id", empresaId);

    if (acoesError) throw acoesError;

    // 3. Arquivar no histórico
    if (fases && fases.length > 0) {
      await registrarHistorico({
        empresa_id: empresaId,
        mandato_id: mandatoId,
        acao: "archived_before_recreate",
        dados_anteriores: {
          fases: fases,
          acoes: acoes || [],
          total_fases: fases.length,
          total_acoes: acoes?.length || 0,
          acoes_concluidas: acoes?.filter((a) => a.concluida).length || 0,
        },
        motivo,
        criado_por: userId,
      });

      console.log(
        `[CronogramaHistorico] Arquivadas ${fases.length} fases e ${acoes?.length || 0} ações`,
      );
    }
  } catch (error) {
    console.error("[CronogramaHistorico] Erro ao arquivar cronograma:", error);
    throw error;
  }
}

/**
 * Confirma uma ação do cronograma (torna imutável)
 */
export async function confirmarAcaoCronograma(
  acaoId: string,
  userId: string,
  userNome: string,
  userEmail: string,
): Promise<void> {
  // 1. Verificar se ação existe e não está confirmada
  const { data: acao, error: fetchError } = await supabase
    .from("cp_acoes_cronograma")
    .select("*, cp_fases(empresa_id)")
    .eq("id", acaoId)
    .single();

  if (fetchError || !acao) {
    throw new Error("Ação não encontrada");
  }

  if (acao.confirmed_at) {
    throw new Error("Esta ação já foi confirmada e não pode ser alterada");
  }

  // 2. Atualizar ação com confirmação
  const { error: updateError } = await supabase
    .from("cp_acoes_cronograma")
    .update({
      concluida: true,
      data_conclusao: new Date().toISOString(),
      confirmed_at: new Date().toISOString(),
      confirmed_by: userId,
      confirmed_by_nome: userNome,
      confirmed_by_email: userEmail,
      updated_at: new Date().toISOString(),
    })
    .eq("id", acaoId);

  if (updateError) {
    console.error("[CronogramaHistorico] Erro ao confirmar ação:", updateError);
    throw new Error("Erro ao confirmar ação");
  }

  // 3. Registrar no histórico
  const empresaId = (acao.cp_fases as any)?.empresa_id || acao.empresa_id;
  if (empresaId) {
    // Buscar mandato ativo da empresa
    const { data: mandato } = await supabase
      .from("cp_cipa_mandato")
      .select("id")
      .eq("empresa_id", empresaId)
      .eq("status", "ativo")
      .single();

    if (mandato) {
      await registrarHistorico({
        empresa_id: empresaId,
        mandato_id: mandato.id,
        acao_id: acaoId,
        acao: "confirmed",
        dados_novos: {
          acao_id: acaoId,
          descricao: acao.descricao,
          confirmed_by_nome: userNome,
          confirmed_by_email: userEmail,
        },
        criado_por: userId,
      });
    }
  }
}

/**
 * Retifica uma ação do cronograma (cria nova versão, preserva original)
 */
export async function retificarAcaoCronograma(
  acaoId: string,
  novosDados: {
    descricao?: string;
    data_planejada?: string;
    observacao?: string;
  },
  motivo: string,
  userId: string,
): Promise<string> {
  // 1. Buscar ação original
  const { data: acaoOriginal, error: fetchError } = await supabase
    .from("cp_acoes_cronograma")
    .select("*, cp_fases(empresa_id)")
    .eq("id", acaoId)
    .single();

  if (fetchError || !acaoOriginal) {
    throw new Error("Ação não encontrada");
  }

  // 2. Marcar original como retificada (não deletar!)
  const { error: updateOriginalError } = await supabase
    .from("cp_acoes_cronograma")
    .update({
      retificada: true,
      retificada_em: new Date().toISOString(),
      motivo_retificacao: motivo,
      updated_at: new Date().toISOString(),
    })
    .eq("id", acaoId);

  if (updateOriginalError) {
    throw new Error("Erro ao marcar ação como retificada");
  }

  // 3. Criar nova versão
  const { data: novaAcao, error: insertError } = await supabase
    .from("cp_acoes_cronograma")
    .insert([
      {
        fase_id: acaoOriginal.fase_id,
        usuario_id: acaoOriginal.usuario_id,
        empresa_id: acaoOriginal.empresa_id,
        descricao: novosDados.descricao || acaoOriginal.descricao,
        obrigatoria: acaoOriginal.obrigatoria,
        ordem: acaoOriginal.ordem,
        tipo_comissao: acaoOriginal.tipo_comissao,
        versao: (acaoOriginal.versao || 1) + 1,
        original_id: acaoOriginal.original_id || acaoId,
        observacao: novosDados.observacao,
      },
    ])
    .select()
    .single();

  if (insertError || !novaAcao) {
    throw new Error("Erro ao criar nova versão da ação");
  }

  // 4. Registrar no histórico
  const empresaId =
    (acaoOriginal.cp_fases as any)?.empresa_id || acaoOriginal.empresa_id;
  if (empresaId) {
    const { data: mandato } = await supabase
      .from("cp_cipa_mandato")
      .select("id")
      .eq("empresa_id", empresaId)
      .eq("status", "ativo")
      .single();

    if (mandato) {
      await registrarHistorico({
        empresa_id: empresaId,
        mandato_id: mandato.id,
        acao_id: novaAcao.id,
        acao: "updated",
        dados_anteriores: {
          acao_id: acaoId,
          descricao: acaoOriginal.descricao,
          versao: acaoOriginal.versao || 1,
        },
        dados_novos: {
          acao_id: novaAcao.id,
          descricao: novaAcao.descricao,
          versao: novaAcao.versao,
        },
        motivo,
        criado_por: userId,
      });
    }
  }

  return novaAcao.id;
}

/**
 * Verifica se uma ação pode ser editada
 */
export function podeEditarAcao(acao: {
  confirmed_at?: string | null;
  concluida?: boolean;
  retificada?: boolean;
}): { permitido: boolean; motivo?: string } {
  if (acao.confirmed_at) {
    return {
      permitido: false,
      motivo:
        'Ação já foi confirmada e está bloqueada para edição. Use "Retificar" para criar uma nova versão.',
    };
  }

  if (acao.retificada) {
    return {
      permitido: false,
      motivo: "Esta ação foi retificada. Edite a versão mais recente.",
    };
  }

  return { permitido: true };
}

/**
 * Obtém histórico de uma empresa/mandato
 */
export async function obterHistorico(
  empresaId: string,
  mandatoId?: string,
  limite: number = 50,
): Promise<CronogramaHistorico[]> {
  let query = supabase
    .from("cp_cronograma_historico")
    .select("*")
    .eq("empresa_id", empresaId)
    .order("created_at", { ascending: false })
    .limit(limite);

  if (mandatoId) {
    query = query.eq("mandato_id", mandatoId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[CronogramaHistorico] Erro ao obter histórico:", error);
    throw new Error("Erro ao obter histórico");
  }

  return data || [];
}

export const cronogramaHistoricoService = {
  registrarHistorico,
  arquivarCronogramaAnterior,
  confirmarAcaoCronograma,
  retificarAcaoCronograma,
  podeEditarAcao,
  obterHistorico,
};

export default cronogramaHistoricoService;
